import BlockingTagsInput from "@/components/blocking-tags-input";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense.ts";
import {
  getCustomerPaymentSources,
  isStripeReadyForPayments,
} from "@/lib/stripe/customer-payments";
import useShopOverrideRules from "@babackapp/private-api-client/client/use-shop-override-rules";
import FormGlobalError from "@babackapp/react-hook-forms-components/form-global-error";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import FormInputRow from "@babackapp/react-hook-forms-components/form-input-row";
import FormOptionsSelector from "@babackapp/react-hook-forms-components/form-option-selector";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import FormTagsInput from "@babackapp/react-hook-forms-components/form-tags-input";
import useTranslation from "@babackapp/translations/use-translation";
import { FormField } from "@babackapp/ui/components/form";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { PortalExchangeFormValues } from "./portal-exchange-card";
import PortalExchangeCollectionForm from "./portal-exchange-collection-form.tsx";
import ExchangeStockLocationSelect from "./exchange-stock-location-select";
import { OverrideRulesManager } from "@/components/override-rules";

type PortalExchangeActivatedFormProps = {
  disabled?: string | boolean;
};

export default function PortalExchangeActivatedForm({
  disabled,
}: PortalExchangeActivatedFormProps = {}) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { control, setValue } = useFormContext<PortalExchangeFormValues>();

  const reshippingHasToBePaid = useWatch({
    control,
    name: "reshippingHasToBePaid",
  });

  const exchangeWithOtherArticles = useWatch({
    control,
    name: "exchangeWithOtherArticles",
  });

  const exchangeCreditRemainingMethod = useWatch({
    control,
    name: "exchangeCreditRemainingMethod",
  });

  const hasDiscountEnabled = shop.discount.available && shop.discount.activated;

  useEffect(() => {
    if (!exchangeWithOtherArticles) {
      setValue("exchangeCreditRemainingMethod", null);
    } else if (!exchangeCreditRemainingMethod) {
      setValue("exchangeCreditRemainingMethod", "customer_choice");
    } else if (
      !hasDiscountEnabled &&
      exchangeCreditRemainingMethod === "discount"
    ) {
      setValue("exchangeCreditRemainingMethod", "customer_choice");
    }
  }, [
    exchangeWithOtherArticles,
    hasDiscountEnabled,
    exchangeCreditRemainingMethod,
    setValue,
  ]);

  const exchangeCreditRemainingMethodOptions = [
    {
      label: t(
        "settings.exchange.fields.exchangeCreditRemainingMethod.options.customer_choice",
      ),
      key: "customer_choice",
    },
    {
      label: t(
        "settings.exchange.fields.exchangeCreditRemainingMethod.options.discount",
      ),
      key: "discount",
    },
    {
      label: t(
        "settings.exchange.fields.exchangeCreditRemainingMethod.options.refund",
      ),
      key: "refund",
    },
  ];

  // BBS-386: read on the values being edited, so the warning shows before
  // saving a setting the portal could not collect.
  const { data: overrideRules = [] } = useShopOverrideRules(shop.id);
  const stripeWarning =
    !disabled &&
    !isStripeReadyForPayments(shop) &&
    getCustomerPaymentSources(
      {
        plan: shop.plan,
        exchange: {
          activated: true,
          exchangeWithOtherArticles,
          reshippingHasToBePaid,
        },
      },
      overrideRules,
    ).length > 0;

  return (
    <>
      <FormField
        control={control}
        name="reserveInventory"
        render={({ field }) => (
          <FormSwitch
            label={t("settings.exchange.fields.reserveInventory.title")}
            description={t(
              "settings.exchange.fields.reserveInventory.description",
            )}
            {...field}
            disabled={disabled}
          />
        )}
      />
      <FormInputRow>
        <FormField
          name="maxDifference"
          control={control}
          render={({ field }) => (
            <FormInput
              label={t("settings.exchange.fields.maxDifference.title")}
              type="number"
              description={t(
                "settings.exchange.fields.maxDifference.description",
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              {...field}
              onChange={(e) => field.onChange(e.target.valueAsNumber)}
              disabled={disabled}
            />
          )}
        />

        <FormField
          control={control}
          name="minimalStock"
          render={({ field }) => (
            <FormInput
              label={t("settings.exchange.fields.minimalStock.title")}
              type="number"
              description={t(
                "settings.exchange.fields.minimalStock.description",
              )}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              {...field}
              onChange={(e) => field.onChange(e.target.valueAsNumber)}
              disabled={disabled}
            />
          )}
        />
      </FormInputRow>
      <FormField
        control={control}
        name="stockLocationId"
        render={({ field }) => (
          <ExchangeStockLocationSelect
            value={field.value ?? null}
            onChange={field.onChange}
            disabled={disabled}
          />
        )}
      />
      <FormField
        control={control}
        name="exchangeAcceptedCountries"
        render={({ field }) => (
          <FormTagsInput
            label={t(
              "settings.exchange.fields.exchangeAcceptedCountries.title",
            )}
            description={t(
              "settings.exchange.fields.exchangeAcceptedCountries.description",
            )}
            {...field}
            disabled={disabled}
          />
        )}
      />
      <FormField
        control={control}
        name="customAttributeKeysToForward"
        render={({ field }) => (
          <FormTagsInput
            label={t(
              "settings.exchange.fields.customAttributeKeysToForward.title",
            )}
            description={t(
              "settings.exchange.fields.customAttributeKeysToForward.description",
            )}
            {...field}
            disabled={disabled}
          />
        )}
      />
      <FormField
        control={control}
        name="tagsForNewOrder"
        render={({ field }) => (
          <FormTagsInput
            label={t("settings.exchange.fields.tagsForNewOrder.title")}
            description={t(
              "settings.exchange.fields.tagsForNewOrder.description",
            )}
            {...field}
            disabled={disabled}
          />
        )}
      />
      <FormField
        control={control}
        name="tagsForNotExchangeOrder"
        render={({ field }) => (
          <BlockingTagsInput
            matches="order-or-product"
            label={t("settings.exchange.fields.tagsForNotExchangeOrder.title")}
            description={t(
              "settings.exchange.fields.tagsForNotExchangeOrder.description",
            )}
            {...field}
            disabled={disabled}
          />
        )}
      />

      <div>
        <FormField
          control={control}
          name="reshippingHasToBePaid"
          render={({ field }) => (
            <FormSwitch
              label={t("settings.exchange.fields.reshippingHasToBePaid.title")}
              description={t(
                "settings.exchange.fields.reshippingHasToBePaid.description",
              )}
              {...field}
              disabled={disabled}
            />
          )}
        />

        <OverrideRulesManager
          actionType={"OVERRIDE_RESHIPPING_PAID"}
          excludeFields={[
            "returnCreatedAt",
            "returnItemCount",
            "returnItemResolutions",
            "returnReasonIds",
          ]}
        />
      </div>
      {shop.plan.exchangeWithOtherArticles && (
        <FormField
          control={control}
          name="exchangeWithOtherArticles"
          render={({ field }) => (
            <FormSwitch
              label={t(
                "settings.exchange.fields.exchangeWithOtherArticles.title",
              )}
              description={t(
                "settings.exchange.fields.exchangeWithOtherArticles.description",
              )}
              {...field}
              disabled={disabled}
            />
          )}
        />
      )}

      {shop.plan.exchangeWithOtherArticles &&
        exchangeWithOtherArticles &&
        hasDiscountEnabled && (
          <FormField
            control={control}
            name="exchangeCreditRemainingMethod"
            render={({ field }) => (
              <FormOptionsSelector
                label={t(
                  "settings.exchange.fields.exchangeCreditRemainingMethod.title",
                )}
                description={t(
                  "settings.exchange.fields.exchangeCreditRemainingMethod.description",
                )}
                options={exchangeCreditRemainingMethodOptions}
                value={field.value}
                onChange={field.onChange}
                disabled={disabled}
              />
            )}
          />
        )}

      <FormField
        control={control}
        name="exchangeOrderAt0"
        render={({ field }) => (
          <FormSwitch
            label={t("settings.exchange.fields.exchangeOrderAt0.title")}
            description={t(
              "settings.exchange.fields.exchangeOrderAt0.description",
            )}
            {...field}
            disabled={disabled}
          />
        )}
      />

      {stripeWarning && (
        <FormGlobalError
          error={t("settings.config.stripeRequiredForCustomerPayments")}
        />
      )}

      {shop.plan.exchangeWithOtherArticles && (
        <FormField
          control={control}
          name="exchangeCreditAcceptedCountries"
          render={({ field }) => (
            <FormTagsInput
              label={t(
                "settings.exchange.fields.exchangeCreditAcceptedCountries.title",
              )}
              description={t(
                "settings.exchange.fields.exchangeCreditAcceptedCountries.description",
              )}
              {...field}
              disabled={disabled}
            />
          )}
        />
      )}

      {shop.plan.exchangeWithOtherArticles && (
        <FormField
          control={control}
          name="tagsForNotExchangeCreditOrder"
          render={({ field }) => (
            <BlockingTagsInput
              matches="order-or-product"
              label={t(
                "settings.exchange.fields.tagsForNotExchangeCreditOrder.title",
              )}
              description={t(
                "settings.exchange.fields.tagsForNotExchangeCreditOrder.description",
              )}
              {...field}
              disabled={disabled}
            />
          )}
        />
      )}

      {shop.plan.exchangeWithOtherArticles && (
        <FormField
          control={control}
          name="exchangeWithOtherArticlesPaymentButtonQuerySelector"
          render={({ field }) => (
            <FormInput
              label={t(
                "settings.exchange.fields.exchangeWithOtherArticlesPaymentButtonQuerySelector.title",
              )}
              description={t(
                "settings.exchange.fields.exchangeWithOtherArticlesPaymentButtonQuerySelector.description",
              )}
              {...field}
              disabled={disabled}
            />
          )}
        />
      )}

      <PortalExchangeCollectionForm disabled={disabled} />
    </>
  );
}
